import React, { Component } from 'react';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useConfig } from '../../contexts/config';
import { reportClientError } from '../../services/moodle/chat';

/**
 * Presentational fallback UI when a component crash occurs.
 * Strictly uses neutral Spanish for user-facing copy.
 */
export const ChatErrorFallback = ({ onRetry }) => {
  return (
    <div
      role="alert"
      className="flex flex-col items-center justify-center p-8 text-center bg-white dark:bg-slate-900 border border-red-200 dark:border-red-900/50 rounded-2xl shadow-sm w-full max-w-md mx-auto my-auto"
    >
      <div className="w-14 h-14 rounded-full bg-red-50 dark:bg-red-950/40 text-red-500 flex items-center justify-center mb-4">
        <AlertCircle size={32} />
      </div>
      <h3 className="text-lg font-bold text-slate-800 dark:text-slate-100 mb-2">
        No se pudo cargar el chat
      </h3>
      <p className="text-sm text-slate-500 dark:text-slate-400 mb-6 max-w-xs">
        Ocurrió un error inesperado al renderizar este componente. Puede intentar recargar la vista.
      </p>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-white bg-red-600 hover:bg-red-700 active:bg-red-800 transition-colors shadow-sm cursor-pointer border-0"
        >
          <RefreshCw size={16} />
          Reintentar
        </button>
      )}
    </div>
  );
};

export class ChatErrorBoundaryClass extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    const { token, baseURL, courseId } = this.props;
    const errorMsg = error?.message || 'React render crash';
    const componentStack = errorInfo?.componentStack || '';
    const payload = `${errorMsg}\n${componentStack}`.trim();

    reportClientError(
      token,
      baseURL,
      courseId,
      'react_render_crash',
      payload
    );
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
    if (this.props.onReset) {
      this.props.onReset();
    }
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }
      return <ChatErrorFallback onRetry={this.handleRetry} error={this.state.error} />;
    }

    return this.props.children;
  }
}

/**
 * Functional wrapper providing auth and config context to the class ErrorBoundary.
 */
export const ChatErrorBoundary = ({ children, courseId, onReset, fallback }) => {
  const { user } = useAuth();
  const { config } = useConfig();

  return (
    <ChatErrorBoundaryClass
      token={user?.token}
      baseURL={config?.baseURL}
      courseId={courseId}
      onReset={onReset}
      fallback={fallback}
    >
      {children}
    </ChatErrorBoundaryClass>
  );
};

export default ChatErrorBoundary;
